/**
 * Panel master-detail para asignar merchants a una regla de límite.
 *
 * @taskId T480
 * @see functional/Specs/specs_cycle_4.md HU-28-FE §Pantalla 4, §Pantalla 5
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CheckCircle2, Loader2, Minus, Plus, Search, X } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  ScrollArea,
  Skeleton,
} from '@flexibilitypay/ui';
import { authFetch } from '@flexibilitypay/shared';

import { useAssignMerchant, useUnassignMerchant, limitKeys } from '../hooks/use-limits';
import type { LimitRuleSummary, MerchantAssignmentSummary } from '../types/limit.types';

export interface MerchantAssignmentPanelProps {
  rule: LimitRuleSummary | null;
  open: boolean;
  onClose: () => void;
  onAssign?: () => void;
  onUnassign?: () => void;
}

export function MerchantAssignmentPanel({
  rule,
  open,
  onClose,
  onAssign,
  onUnassign,
}: MerchantAssignmentPanelProps) {
  const { t } = useTranslation(['limits', 'common']);
  const queryClient = useQueryClient();

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Toast confirmación
  const [successKey, setSuccessKey] = useState<'assigned' | 'unassigned' | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Fetch merchants desde BE
  const { data: merchantData, isLoading } = useQuery({
    queryKey: limitKeys.merchants(rule?.id ?? ''),
    queryFn: async () => {
      const res = await authFetch(`/api/v1/bo/limit-rules/${rule!.id}/merchants`);
      if (!res.ok) throw new Error('Failed to load merchants');
      return res.json() as Promise<unknown>;
    },
    enabled: open && rule !== null,
  });

  // Procesar respuesta flexible (array o { merchants: [], assigned: [] })
  const merchants = useMemo((): MerchantAssignmentSummary[] => {
    if (!merchantData) return [];
    const list = Array.isArray(merchantData)
      ? merchantData
      : ((merchantData as Record<string, unknown>).merchants ?? merchantData);
    if (!Array.isArray(list)) return [];
    const assignedIds = new Set<string>(
      Array.isArray(merchantData)
        ? []
        : ((merchantData as Record<string, unknown>).assigned as string[] ?? []),
    );
    return (list as Array<{ id: string; razonSocial: string; cuit: string; tipo?: string; assignmentId?: string }>).map((m) => ({
      ...m,
      isAssigned: assignedIds.has(m.id),
    }));
  }, [merchantData]);

  // Mutations
  const assignMutation = useAssignMerchant();
  const unassignMutation = useUnassignMerchant();
  const isMutating = assignMutation.isPending || unassignMutation.isPending;

  // Split merchants into assigned and available
  const { assigned, available } = useMemo(() => {
    const filtered = debouncedSearch
      ? merchants.filter(
          (m) =>
            m.razonSocial.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
            m.cuit.includes(debouncedSearch),
        )
      : merchants;
    return {
      assigned: filtered.filter((m) => m.isAssigned),
      available: filtered.filter((m) => !m.isAssigned),
    };
  }, [merchants, debouncedSearch]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  // Assign merchant
  const handleAssign = useCallback(
    (merchant: MerchantAssignmentSummary) => {
      if (!rule) return;
      assignMutation.mutate(
        { ruleId: rule.id, payload: { merchantId: merchant.id } },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: limitKeys.merchants(rule.id) });
            setSuccessKey('assigned');
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setSuccessKey(null), 3000);
            onAssign?.();
          },
        },
      );
    },
    [rule, assignMutation, queryClient, onAssign],
  );

  // Unassign merchant
  const handleUnassign = useCallback(
    (merchant: MerchantAssignmentSummary) => {
      if (!rule) return;
      unassignMutation.mutate(
        { ruleId: rule.id, assignmentId: merchant.assignmentId ?? merchant.id },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: limitKeys.merchants(rule.id) });
            setSuccessKey('unassigned');
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setSuccessKey(null), 3000);
            onUnassign?.();
          },
        },
      );
    },
    [rule, unassignMutation, queryClient, onUnassign],
  );

  if (!rule) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen: boolean) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {t('limits:assignment.title', 'Asignar Merchants a Regla')}
          </DialogTitle>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-medium">{rule.nombre}</span>
            <Badge variant="outline">{rule.aplicaA}</Badge>
          </div>
        </DialogHeader>

        {/* Toast confirmación asignación/desasignación */}
        {successKey && (
          <div
            role="status"
            aria-live="polite"
            data-testid="assignment-success-toast"
            className="flex items-center gap-2 rounded-md border border-emerald-700 bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow-md"
          >
            <CheckCircle2 className="h-4 w-4 text-white" aria-hidden />
            <span>
              {successKey === 'assigned'
                ? t('limits:assignment.successAssigned', 'Merchant asignado correctamente')
                : t('limits:assignment.successUnassigned', 'Merchant desvinculado correctamente')}
            </span>
          </div>
        )}

        {/* Search */}
        <div className="py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('limits:assignment.searchPlaceholder', 'Buscar merchant por nombre o CUIT...')}
              className="pl-10"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setDebouncedSearch('');
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Master-detail layout */}
        <div className="flex-1 grid grid-cols-2 gap-4 overflow-hidden">
          {/* Panel izquierdo: Asignados */}
          <Card className="flex flex-col overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Check className="h-4 w-4 text-green-500" />
                {t('limits:assignment.assigned', 'Asignados')}
                <Badge variant="secondary">{assigned.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-0">
              {isLoading ? (
                <div className="p-4 space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : assigned.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  {t('limits:assignment.noAssigned', 'Sin merchants vinculados.')}
                </div>
              ) : (
                <ScrollArea className="h-full">
                  <div className="p-2 space-y-2">
                    {assigned.map((merchant) => (
                      <Card key={merchant.id} className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{merchant.razonSocial}</p>
                            <p className="text-xs text-muted-foreground">{merchant.cuit}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnassign(merchant)}
                            disabled={isMutating}
                            className="text-destructive hover:text-destructive"
                          >
                            {unassignMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Minus className="h-4 w-4" />
                            )}
                            <span className="ml-1">{t('common:remove', 'Quitar')}</span>
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Panel derecho: Disponibles */}
          <Card className="flex flex-col overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Plus className="h-4 w-4 text-blue-500" />
                {t('limits:assignment.available', 'Disponibles')}
                <Badge variant="secondary">{available.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-0">
              {isLoading ? (
                <div className="p-4 space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : available.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  {searchTerm
                    ? t('limits:assignment.noMatchingAvailable', 'No se encontraron merchants disponibles.')
                    : t('limits:assignment.allAssigned', 'Todos los merchants están asignados.')}
                </div>
              ) : (
                <ScrollArea className="h-full">
                  <div className="p-2 space-y-2">
                    {available.map((merchant) => (
                      <Card key={merchant.id} className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{merchant.razonSocial}</p>
                            <p className="text-xs text-muted-foreground">{merchant.cuit}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAssign(merchant)}
                            disabled={isMutating}
                            className="text-primary hover:text-primary"
                          >
                            {assignMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Plus className="h-4 w-4" />
                            )}
                            <span className="ml-1">{t('common:add', 'Asignar')}</span>
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onClose}>{t('common:done', 'Listo')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
